import React, { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

type TabButtonsProps<A, B> = {
  options: [{ label: string; value: A }, { label: string; value: B }];
  initialState?: A | B;
  onTabChange?: (tab: A | B) => void;
  buttonClassName?: string;
  buttonSelectedClassName?: string;
  containerClassName?: string;
  highlightClassName?: string;
};

export default function TabButtons<A, B>({
  initialState,
  options,
  onTabChange,
  buttonSelectedClassName,
  highlightClassName,
  containerClassName,
  buttonClassName,
}: TabButtonsProps<A, B>): JSX.Element {
  const [activeTab, setActiveTab] = React.useState<A | B>(
    initialState === undefined ? options[0].value : initialState,
  );

  useEffect(() => {
    onTabChange?.(activeTab);
  }, [activeTab]);

  return (
    <div className={twMerge('relative flex rounded-[8px] bg-secondary-200 p-1.5', containerClassName)}>
      <TabButton
        className={activeTab === options[0].value ? buttonSelectedClassName : buttonClassName}
        isSelected={activeTab === options[0].value}
        onClick={() => setActiveTab(options[0].value)}>
        {options[0].label}
      </TabButton>
      <TabButton
        className={activeTab === options[1].value ? buttonSelectedClassName : buttonClassName}
        isSelected={activeTab === options[1].value}
        onClick={() => setActiveTab(options[1].value)}>
        {options[1].label}
      </TabButton>
      <div
        className={twMerge(
          `absolute h-[calc(100%-0.75rem)] w-[calc(50%-0.375rem)] rounded-[8px] bg-surface px-6 py-2 shadow-sm transition-all ${activeTab === options[0].value ? 'translate-x-0' : 'translate-x-full'}`,
          highlightClassName,
        )}
      />
    </div>
  );
}

type TabButtonProps = {
  className?: string;
  isSelected: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

function TabButton({ className, isSelected, ...props }: TabButtonProps): JSX.Element {
  return (
    <button
      className={twMerge(
        `z-10 w-max flex-1 whitespace-nowrap rounded-[8px] px-6 py-2 ${isSelected ? 'font-semibold' : 'opacity-60'}`,
        className,
      )}
      type="button"
      {...props}
    />
  );
}
